import React, { useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import StudentAvatar from '../../../shared/StudentAvatarView';
import useBookApprovalCelebration from '../../../mini-tasks/components/useBookApprovalCelebration';

const formatSubmittedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Istanbul' }).format(date);
};

export default function LibraryBookApprovalList({ claims = [], onReview, smartboard = false, onBack }) {
  const lock = useRef(new Set());
  const [busyIds, setBusyIds] = useState(new Set());
  const { celebrations, prepare, celebrate, mounted } = useBookApprovalCelebration(smartboard);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [form, setForm] = useState({});
  const visibleClaims = useMemo(() => [...claims, ...Object.values(celebrations).filter((claim) => !claims.some((item) => item.id === claim.id))], [claims, celebrations]);
  const sortedClaims = useMemo(() => [...visibleClaims].sort((a, b) =>
    (Date.parse(a.submitted_at) || 0) - (Date.parse(b.submitted_at) || 0) || Number(a.id) - Number(b.id)), [visibleClaims]);
  const rowBusy = (id) => smartboard ? busyIds.has(id) : busyId !== null;
  const dialogBusy = smartboard ? busyIds.has(editing?.id ?? rejecting?.id) : busyId !== null;

  const review = async (claim, action, corrections) => {
    if (lock.current.has(claim.id) || (!smartboard && lock.current.size)) return;
    prepare();
    lock.current.add(claim.id);
    setBusyIds(new Set(lock.current));
    setBusyId(claim.id);
    setError('');
    try {
      const result = await onReview(claim.id, action, corrections);
      if (!mounted.current) return;
      if (action === 'approve') celebrate(claim, result);
      setEditing((current) => current?.id === claim.id ? null : current);
      setRejecting((current) => current?.id === claim.id ? null : current);
    } catch (err) {
      if (mounted.current) setError(err.message || 'İşlem tamamlanamadı.');
    } finally {
      lock.current.delete(claim.id);
      if (mounted.current) { setBusyIds(new Set(lock.current)); setBusyId(null); }
    }
  };

  const openEdit = (claim) => {
    setError('');
    setEditing(claim);
    setForm({ title: claim.title, author: claim.author || '', page_count: claim.page_count ?? '', finished_date: claim.finished_date || '' });
  };

  const approveEdited = (event) => {
    event.preventDefault();
    const pages = String(form.page_count).trim();
    if (!form.title.trim()) { setError('Kitap adı zorunludur.'); return; }
    if (pages && (!Number.isInteger(Number(pages)) || Number(pages) < 1 || Number(pages) > 5000)) {
      setError('Sayfa sayısı 1 ile 5000 arasında bir tam sayı olmalıdır.'); return;
    }
    review(editing, 'approve', {
      title: form.title.trim(), author: form.author.trim() || null,
      page_count: pages ? Number(pages) : null,
      ...(editing.finished_date && form.finished_date ? { finished_date: form.finished_date } : {}),
    });
  };

  return (
    <>
      {error && !editing && !rejecting && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {visibleClaims.length === 0 ? (
        <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, textAlign: 'center' }}>
          <MenuBookOutlinedIcon sx={{ fontSize: 44, color: '#8eb3d5', mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>{smartboard ? "Tüm kitaplar incelendi! 🎉" : "Bu şubede onay bekleyen kitap bulunmuyor."}</Typography>
          {smartboard && <><Typography sx={{ my: 2 }}>Şu anda onay bekleyen kitap bulunmuyor.</Typography><Button variant="contained" onClick={onBack}>Kütüphanem'e Dön</Button></>}
        </Paper>
      ) : (
        <Stack className="library-approval-list" spacing={1.5}>
          {sortedClaims.map((claim) => {
            const submittedDate = formatSubmittedDate(claim.submitted_at);
            return (
              <Paper className={`library-approval-row${celebrations[claim.id] ? " board-book-approved" : ""}`} component="article" aria-label={`${claim.student_name}: ${claim.title}`} key={claim.id}>
                <Box className="library-approval-row__student">
                  <StudentAvatar studentId={claim.student_id} avatarId={claim.avatar_id} starterEmoji={claim.starter_emoji} name={claim.student_name} size={52} />
                  <Typography className="library-approval-row__name">{claim.student_name}</Typography>
                </Box>
                <Box className="library-approval-row__book">
                  <Box className="library-neutral-cover" aria-hidden="true"><MenuBookOutlinedIcon /></Box>
                  <Box className="library-approval-row__book-copy">
                    <Typography component="h3">{claim.title}</Typography>
                    {claim.page_count != null && <Typography className="library-approval-row__pages">{claim.page_count} sayfa</Typography>}
                    {submittedDate && <Typography className="library-approval-row__date"><CalendarTodayOutlinedIcon />Talep: {submittedDate}</Typography>}
                  </Box>
                </Box>
                <Box className="library-approval-row__actions">
                  {celebrations[claim.id] ? <Box className="board-book-celebration" role="status"><span className="board-book-stars" aria-hidden="true">⭐ ⭐ ⭐</span><strong>+5 ⭐</strong><span>Kitap onaylandı!</span></Box> : <>
                  <Button className="library-primary" variant="contained" disabled={rowBusy(claim.id)} onClick={() => review(claim, 'approve')}>
                    {rowBusy(claim.id) ? 'İşleniyor…' : 'Onayla'}
                  </Button>
                  <Button variant="outlined" disabled={rowBusy(claim.id)} onClick={() => openEdit(claim)}>Düzenle ve Onayla</Button>
                  <Button disabled={rowBusy(claim.id)} onClick={() => { setError(''); setRejecting(claim); }}>Reddet</Button>
                  </>}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
      <Dialog open={Boolean(editing)} onClose={() => !dialogBusy && setEditing(null)} fullWidth maxWidth="sm" aria-labelledby="book-approval-edit-title" PaperProps={{ className: smartboard ? "board-book-dialog" : undefined, sx: { borderRadius: 4 } }}>
        <Box component="form" onSubmit={approveEdited}>
          <DialogTitle id="book-approval-edit-title" sx={{ fontWeight: 800 }}>Düzenle ve Onayla</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{editing?.student_name}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField autoFocus required label="Kitap adı" value={form.title || ''} inputProps={{ maxLength: 256 }} disabled={dialogBusy} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <TextField label="Yazar" value={form.author || ''} inputProps={{ maxLength: 256 }} helperText="İsteğe bağlı" disabled={dialogBusy} onChange={(e) => setForm({ ...form, author: e.target.value })} />
              <TextField label="Sayfa sayısı" type="number" value={form.page_count ?? ''} inputProps={{ min: 1, max: 5000, step: 1 }} helperText="İsteğe bağlı" disabled={dialogBusy} onChange={(e) => setForm({ ...form, page_count: e.target.value })} />
              {editing?.finished_date && <TextField label="Tamamlanma Tarihi" type="date" InputLabelProps={{ shrink: true }} value={form.finished_date || ''} disabled={dialogBusy} onChange={(e) => setForm({ ...form, finished_date: e.target.value })} />}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button disabled={dialogBusy} onClick={() => setEditing(null)}>Vazgeç</Button>
            <Button type="submit" disabled={dialogBusy} variant="contained">{dialogBusy ? 'Kaydediliyor…' : 'Kaydet ve Onayla'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={Boolean(rejecting)} onClose={() => !dialogBusy && setRejecting(null)} fullWidth maxWidth="xs" aria-labelledby="book-approval-reject-title" PaperProps={{ className: smartboard ? "board-book-dialog" : undefined, sx: { borderRadius: 4 } }}>
        <DialogTitle id="book-approval-reject-title" sx={{ fontWeight: 800 }}>Talebi Reddet</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ mb: 2 }}>Bu kitap tamamlama talebini reddetmek istiyor musunuz?</Typography>
          <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{rejecting?.student_name}</Typography>
          <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{rejecting?.title}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={dialogBusy} onClick={() => setRejecting(null)}>Vazgeç</Button>
          <Button disabled={dialogBusy} color="error" variant="contained" onClick={() => review(rejecting, 'reject')}>{dialogBusy ? 'İşleniyor…' : 'Talebi Reddet'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
